import type { QueryClient } from '@tanstack/react-query';

import ApiError from '~/types/ApiError';
import type { Attraction, AttractionCanonicalizedErrorResponse } from '~/types/attractionTypes';

import { moveCacheBuster } from './attractionCacheBuster';
import { attractionQueryKeys } from './attractionQueryKeys';

export type CanonicalAttractionResolution = {
  loserAttractionId: string;
  canonicalAttractionId: string;
};

function hasCanonicalizedErrorShape(body: unknown): body is AttractionCanonicalizedErrorResponse {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { error?: unknown }).error === 'attraction_canonicalized' &&
    typeof (body as { loserAttractionId?: unknown }).loserAttractionId === 'string' &&
    typeof (body as { canonicalAttractionId?: unknown }).canonicalAttractionId === 'string'
  );
}

function getCanonicalizedErrorBody(body: unknown): AttractionCanonicalizedErrorResponse | null {
  if (hasCanonicalizedErrorShape(body)) {
    return body;
  }

  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const errors = (body as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) {
    return null;
  }

  return errors.find(hasCanonicalizedErrorShape) ?? null;
}

export function getCanonicalAttractionResolution(error: unknown): CanonicalAttractionResolution | null {
  if (!(error instanceof ApiError) || error.status !== 409) {
    return null;
  }

  const canonicalizedBody = getCanonicalizedErrorBody(error.responseBody);
  if (!canonicalizedBody) {
    return null;
  }

  return {
    loserAttractionId: canonicalizedBody.loserAttractionId,
    canonicalAttractionId: canonicalizedBody.canonicalAttractionId,
  };
}

export function getReadResolvedAttractionId(attraction: Attraction | null | undefined, requestedAttractionId: string) {
  if (!attraction) {
    return null;
  }

  const resolvedId = attraction.canonicalAttractionId ?? attraction.attractionId;
  const sourceId = attraction.resolvedFromAttractionId ?? requestedAttractionId;

  if (!resolvedId || resolvedId === sourceId || resolvedId === requestedAttractionId) {
    return null;
  }

  return resolvedId;
}

export function retargetAttractionCache({
  queryClient,
  fromAttractionId,
  toAttractionId,
  attraction,
}: {
  queryClient: QueryClient;
  fromAttractionId: string;
  toAttractionId: string;
  attraction?: Attraction | null;
}) {
  if (!fromAttractionId || !toAttractionId || fromAttractionId === toAttractionId) {
    return;
  }

  moveCacheBuster(fromAttractionId, toAttractionId);

  if (attraction) {
    queryClient.setQueryData(attractionQueryKeys.detail(toAttractionId), attraction);
  }

  queryClient.removeQueries({ queryKey: attractionQueryKeys.detail(fromAttractionId) });
  queryClient.invalidateQueries({ queryKey: attractionQueryKeys.detail(toAttractionId) });
  queryClient.invalidateQueries({ queryKey: attractionQueryKeys.offers(toAttractionId) });
  queryClient.invalidateQueries({ queryKey: attractionQueryKeys.images(toAttractionId) });
  queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });
}
